import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

/**
 * Contenedor con auto-ajuste para respetar el paradigma "nunca scroll":
 * - Si TODAS las paginas caben en el viewport, las muestra juntas (una sola
 *   pantalla, sin paginador) — lo ideal.
 * - Si no caben (p.ej. movil), pagina: muestra una pagina y un paginador.
 * La decision se remide en cada resize.
 *
 * @param {React.ReactNode[]} pages - una pagina por elemento del array.
 * @param {number} gap - separacion entre paginas cuando se muestran juntas.
 */
function PagedView({ pages, gap = 2, stack = false }) {
    const areaRef = useRef(null);
    const stackRef = useRef(null);
    const [paginate, setPaginate] = useState(false);
    const [measuring, setMeasuring] = useState(true);
    const [index, setIndex] = useState(0);

    useLayoutEffect(() => {
        // `stack`: apila todo sin paginar (movil), la pagina hace scroll.
        if (stack) {
            setPaginate(false);
            setMeasuring(false);
            return;
        }
        if (!measuring) return;
        const area = areaRef.current;
        const stackEl = stackRef.current;
        if (area && stackEl) {
            const overflow = stackEl.scrollHeight > area.clientHeight + 1;
            setPaginate(overflow && pages.length > 1);
        }
        setMeasuring(false);
    }, [measuring, pages.length, stack]);

    useEffect(() => {
        const onResize = () => {
            setIndex(0);
            setMeasuring(true);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const showAll = measuring || !paginate;
    const count = pages.length;
    const go = (i) => setIndex(Math.max(0, Math.min(count - 1, i)));

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box ref={areaRef} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                {showAll ? (
                    <Box ref={stackRef} sx={{ display: "flex", flexDirection: "column", gap }}>
                        {pages.map((p, i) => (
                            <Box key={i}>{p}</Box>
                        ))}
                    </Box>
                ) : (
                    pages[index]
                )}
            </Box>

            {!measuring && paginate && (
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1, pt: 1.5 }}>
                    <IconButton size="small" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Anterior">
                        <ChevronLeftRoundedIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                        {pages.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => go(i)}
                                sx={{
                                    width: i === index ? 22 : 8,
                                    height: 8,
                                    borderRadius: 999,
                                    cursor: "pointer",
                                    bgcolor: i === index ? "primary.main" : "divider",
                                    transition: "width 0.2s ease, background-color 0.2s ease",
                                }}
                            />
                        ))}
                    </Box>
                    <IconButton size="small" onClick={() => go(index + 1)} disabled={index === count - 1} aria-label="Siguiente">
                        <ChevronRightRoundedIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

export default PagedView;
